"use client";

import { useState, useSyncExternalStore } from "react";
import { CalendarPlus, ChevronDown } from "lucide-react";
import { useFavorieten } from "@/components/favorieten";
import { cn } from "@/lib/utils";

// De kalender in je eigen agenda (helpdesk #143); zie app/agenda.ics.
//
// Abonneren in plaats van downloaden: dan haalt de agenda van de telefoon de
// lijst zelf opnieuw op, en krijgt een wedstrijd vanzelf een tijd zodra TRS
// die kent. Een iPhone of Mac doet dat met een webcal-link. Android kent die
// niet; daar gaat het via Google Agenda, die dezelfde lijst kan volgen.
//
// In tokens en niet in de vaste kleurcodes van de rest van de pagina; zie regel
// 2 in AGENTS.md.

const nooit = () => () => {};

function useIsAndroid(): boolean {
  return useSyncExternalStore(
    nooit,
    () => /android/i.test(navigator.userAgent),
    () => false,
  );
}

function adressen(zoek: string) {
  const pad = `/agenda.ics${zoek}`;
  const host = typeof window === "undefined" ? "" : window.location.host;
  const webcal = `webcal://${host}${pad}`;
  return {
    webcal,
    google: `https://calendar.google.com/calendar/r?cid=${encodeURIComponent(webcal)}`,
    bestand: pad,
  };
}

export function AgendaAbonnement() {
  const [open, setOpen] = useState(false);
  const android = useIsAndroid();
  const { perSoort, geladen } = useFavorieten();
  const klassen = geladen ? perSoort("klasse") : [];

  const keuzes = [
    { titel: "Alle wedstrijden", uitleg: null as string | null, zoek: "" },
    ...(klassen.length > 0
      ? [
          {
            titel: klassen.length === 1 ? `Alleen ${klassen[0].naam}` : `Alleen je ${klassen.length} klassen`,
            uitleg:
              "Welke klassen er op een wedstrijd rijden zet NTTO er meestal pas een paar weken vooraf in. Tot dan blijft deze agenda leeg.",
            zoek: `?klassen=${klassen.flatMap((k) => (k.soort === "klasse" ? [k.sleutel] : [])).join(",")}`,
          },
        ]
      : []),
  ];

  return (
    <div className="mb-6 rounded-xl border border-lijn bg-card">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center gap-3 px-4 py-3 text-left"
      >
        <CalendarPlus className="size-5 shrink-0 text-primary" aria-hidden />
        <span className="min-w-0 flex-1">
          <span className="block font-display font-black uppercase text-foreground">In je eigen agenda</span>
          <span className="block text-xs text-muted-foreground">
            Blijft vanzelf bij als er iets verandert
          </span>
        </span>
        <ChevronDown
          className={cn("size-4 shrink-0 text-muted-foreground transition-transform", open && "rotate-180")}
          aria-hidden
        />
      </button>

      {open && (
        <div className="space-y-4 border-t border-border px-4 py-4">
          {keuzes.map((k) => {
            const a = adressen(k.zoek);
            return (
              <div key={k.titel}>
                <p className="font-semibold text-foreground">{k.titel}</p>
                {k.uitleg && <p className="mt-0.5 text-xs text-muted-foreground">{k.uitleg}</p>}
                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2">
                  <a
                    href={android ? a.google : a.webcal}
                    target={android ? "_blank" : undefined}
                    rel={android ? "noopener" : undefined}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 font-display text-sm font-black uppercase text-primary-foreground"
                  >
                    <CalendarPlus className="size-4" aria-hidden />
                    {android ? "Toevoegen aan Google Agenda" : "Abonneren"}
                  </a>
                  <a href={a.bestand} download className="text-sm text-muted-foreground underline">
                    of download het bestand
                  </a>
                </div>
              </div>
            );
          })}
          <p className="text-xs text-muted-foreground">
            Staat er nog geen tijd bij een wedstrijd, dan komt hij als hele dag in je agenda. Zodra
            de tijd bekend is, past je agenda dat zelf aan. Een download blijft staan zoals hij is.
          </p>
        </div>
      )}
    </div>
  );
}
